import DOMPurify from "dompurify";
import { marked } from "marked";
import type { FC, SVGProps } from "react";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { UIMessage, useChatContext } from "@/client/context/ChatContext";
import {
  AddIcon,
  DeleteIcon,
  EditIcon,
  GroceryIcon,
  HistoryIcon,
  PlanIcon,
  RecipeIcon,
  ScrollDownIcon,
  SendIcon,
  StockIcon,
} from "@/client/icons";
import { Conversation } from "@/client/types";

// ─── Markdown rendering ───────────────────────────────────────────────────────

const ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "em",
  "a",
  "ul",
  "ol",
  "li",
  "h1",
  "h2",
  "h3",
  "h4",
  "code",
  "pre",
  "blockquote",
  "hr",
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
];

function renderMarkdown(text: string): string {
  if (!text) return "";
  const rawHtml = marked.parse(text, { gfm: true, breaks: false }) as string;
  return DOMPurify.sanitize(rawHtml, { ALLOWED_TAGS, ALLOWED_ATTR: ["href", "target"] });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function TypingIndicator() {
  return (
    <div className="self-start bg-term-surface border border-term-border rounded-2xl rounded-bl-sm shadow-sm px-4 py-3 flex items-center gap-1.5">
      <span className="typing-dot" style={{ animationDelay: "0s" }} />
      <span className="typing-dot" style={{ animationDelay: "0.15s" }} />
      <span className="typing-dot" style={{ animationDelay: "0.3s" }} />
    </div>
  );
}

function ToolCallsAccordion({ toolCalls }: { toolCalls: { name: string; summary: string }[] }) {
  const [open, setOpen] = useState(false);
  if (!toolCalls.length) return null;
  return (
    <div className={`queries-accordion my-1 text-xs${open ? " open" : ""}`}>
      <div
        className="queries-header flex items-center gap-1.5 py-0.5 cursor-pointer text-term-text-muted hover:text-term-text-dim select-none"
        onClick={() => setOpen((o) => !o)}
      >
        <span>
          {toolCalls.length} tool call{toolCalls.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="queries-content">
        {toolCalls.map((tc, i) => (
          <pre
            key={i}
            className="my-1 p-2 bg-term-bg border border-term-border rounded text-xs text-term-text-dim overflow-x-auto whitespace-pre-wrap selectable"
          >
            <span className="text-term-accent">{tc.name}</span> {tc.summary}
          </pre>
        ))}
      </div>
    </div>
  );
}

function MessageBubble({
  message,
  onEdit,
  onRetry,
}: {
  message: UIMessage;
  onEdit: (idx: number, content: string) => void;
  onRetry?: () => void;
}) {
  const isUser = message.role === "user";
  const isError = message.role === "error";

  if (isError) {
    return (
      <div className="flex flex-col max-w-[85%] self-start">
        <div className="relative px-5 py-3.5 text-base leading-relaxed rounded-2xl rounded-bl-sm bg-term-error/8 border border-term-error/25 selectable">
          <p className="text-term-error font-medium text-sm mb-1">Algo salió mal</p>
          <p className="text-term-text-dim text-base">
            {message.content.replace(/^Error:\s*/i, "")}
          </p>
        </div>
        {onRetry && (
          <button
            onClick={onRetry}
            className="self-start mt-1.5 text-sm text-term-error hover:text-term-accent cursor-pointer transition-colors flex items-center gap-1 font-medium"
          >
            Reintentar
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`flex flex-col max-w-[85%] ${isUser ? "self-end" : "self-start"}`}>
      <div
        className={`relative px-5 py-3.5 text-base leading-relaxed shadow-sm selectable ${
          isUser
            ? "bg-term-user text-white rounded-2xl rounded-br-sm"
            : "bg-term-surface border border-term-border text-term-text rounded-2xl rounded-bl-sm"
        }`}
      >
        {isUser ? (
          <div className="whitespace-pre-wrap">{message.content}</div>
        ) : (
          <div
            className="message-body"
            dangerouslySetInnerHTML={{ __html: renderMarkdown(message.content) }}
          />
        )}
        {message.toolCalls && message.toolCalls.length > 0 && (
          <ToolCallsAccordion toolCalls={message.toolCalls} />
        )}
      </div>
      {isUser && (
        <button
          onClick={() => onEdit(message.userMsgIndex!, message.content)}
          className="self-end mt-0.5 text-sm text-term-text-muted hover:text-term-accent cursor-pointer transition-colors flex items-center gap-1 py-0.5"
        >
          <EditIcon width={12} height={12} />
          editar
        </button>
      )}
    </div>
  );
}

const WELCOME_SUGGESTIONS: { Icon: FC<SVGProps<SVGSVGElement>>; text: string }[] = [
  { Icon: StockIcon, text: "Mostrame el stock actual" },
  { Icon: PlanIcon, text: "Planificar la semana" },
  { Icon: RecipeIcon, text: "Agregar una receta nueva" },
  { Icon: GroceryIcon, text: "Armar lista de compras" },
];

function WelcomeScreen({ onSuggestion }: { onSuggestion: (text: string) => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 gap-6">
      <h2 className="font-serif text-2xl text-term-accent">¿Qué cocinamos hoy?</h2>
      <p className="text-term-text-dim text-base">Preguntame lo que quieras sobre meal prep</p>
      <div className="grid grid-cols-2 gap-3 mt-2 w-full max-w-md">
        {WELCOME_SUGGESTIONS.map(({ Icon, text }, i) => (
          <button
            key={text}
            className="flex items-center gap-2.5 px-4 py-3 text-base text-left text-term-text-dim bg-term-surface border border-term-border rounded-xl cursor-pointer hover:border-term-accent-dim hover:text-term-text hover:shadow-md transition-all shadow-sm animate-[fade-in-up_0.3s_ease_both]"
            style={{ animationDelay: `${i * 50}ms` }}
            onClick={() => onSuggestion(text)}
            type="button"
          >
            <Icon width={18} height={18} className="shrink-0 text-term-text-muted" aria-hidden />
            <span>{text}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── History sidebar ──────────────────────────────────────────────────────────

function HistorySidebar({
  conversations,
  activeId,
  onSelect,
  onDelete,
  onNew,
}: {
  conversations: Conversation[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onNew: () => void;
}) {
  return (
    <div className="flex flex-col h-full border-r border-term-border bg-term-surface">
      <div className="flex items-center justify-between px-4 py-3 border-b border-term-border">
        <span className="text-xs font-medium text-term-text-dim uppercase tracking-wide">
          Conversaciones
        </span>
        <button
          onClick={onNew}
          className="flex items-center gap-1 px-2 py-1 text-sm text-term-text-muted hover:text-term-accent hover:bg-term-accent/10 rounded-lg cursor-pointer transition-all"
        >
          <AddIcon width={12} height={12} />
          Nueva
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5">
        {conversations.length === 0 && (
          <p className="text-sm text-term-text-muted text-center py-4">Sin conversaciones</p>
        )}
        {conversations.map((c) => (
          <div
            key={c.id}
            className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all text-sm ${
              c.id === activeId
                ? "bg-term-accent/10 text-term-accent"
                : "text-term-text-dim hover:bg-term-raised hover:text-term-text"
            }`}
            onClick={() => onSelect(c.id)}
          >
            <span className="flex-1 truncate">{c.title || "Sin título"}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDelete(c.id);
              }}
              className="shrink-0 md:opacity-0 md:group-hover:opacity-100 focus-visible:opacity-100 text-term-text-muted hover:text-term-error cursor-pointer transition-all"
              aria-label="Borrar"
            >
              <DeleteIcon width={12} height={12} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function ChatPage() {
  const {
    uiMessages,
    isLoading,
    conversations,
    activeConversationId,
    pendingEdit,
    sendMessage,
    editMessage,
    startNewChat,
    loadConversation,
    deleteConversation,
    clearPendingEdit,
    reloadConversations,
    retryLastMessage,
  } = useChatContext();

  const [inputValue, setInputValue] = useState("");
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatWrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);

  useEffect(() => {
    void reloadConversations();
  }, [reloadConversations]);

  useEffect(() => {
    if (pendingEdit !== null) {
      setInputValue(pendingEdit); // eslint-disable-line react-hooks/set-state-in-effect -- sync from context
      clearPendingEdit();
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [pendingEdit, clearPendingEdit]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [uiMessages, isLoading]);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 100);
  }, []);

  const handleScroll = useCallback(() => {
    const el = chatWrapperRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowScrollBtn(dist > 150);
  }, []);

  const handleSend = useCallback(async () => {
    const text = inputValue.trim();
    if (!text || isLoading) return;
    setInputValue("");
    await sendMessage(text);
  }, [inputValue, isLoading, sendMessage]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        void handleSend();
      }
    },
    [handleSend],
  );

  const handleSuggestion = useCallback(
    (text: string) => {
      void sendMessage(text);
    },
    [sendMessage],
  );

  const handleConversationSelect = useCallback(
    async (id: string) => {
      await loadConversation(id);
      window.history.pushState(null, "", `/c/${id}`);
      setShowHistory(false);
    },
    [loadConversation],
  );

  const handleNewChat = useCallback(() => {
    startNewChat();
    window.history.pushState(null, "", "/");
    setShowHistory(false);
  }, [startNewChat]);

  return (
    <div className="flex h-full">
      {/* History sidebar — overlay on mobile, inline on desktop */}
      {showHistory && (
        <>
          <div
            className="md:hidden fixed inset-0 bg-black/50 z-40"
            onClick={() => setShowHistory(false)}
          />
          <div className="fixed inset-y-0 left-0 w-72 z-50 md:relative md:inset-auto md:w-64 md:z-auto shrink-0">
            <HistorySidebar
              conversations={conversations}
              activeId={activeConversationId}
              onSelect={handleConversationSelect}
              onDelete={deleteConversation}
              onNew={handleNewChat}
            />
          </div>
        </>
      )}

      {/* Main chat area */}
      <div className="flex-1 min-w-0 overflow-y-auto" ref={chatWrapperRef} onScroll={handleScroll}>
        {/* Header — sticky top, content scrolls behind */}
        <div className="sticky top-0 z-10 flex items-center gap-1 px-4 py-2.5 shadow-[0_1px_3px_-1px_rgba(0,0,0,0.06)] bg-term-bg/80 backdrop-blur-md">
          <button
            onClick={() => setShowHistory((s) => !s)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg cursor-pointer transition-all${
              showHistory
                ? " text-term-accent bg-term-accent/10"
                : " text-term-text-muted hover:text-term-text hover:bg-term-raised"
            }`}
            aria-label="Historial"
          >
            <HistoryIcon width={15} height={15} />
            Historial
          </button>

          <button
            onClick={handleNewChat}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-term-text-muted hover:text-term-accent hover:bg-term-accent/10 rounded-lg cursor-pointer transition-all"
            aria-label="Nueva conversación"
          >
            <AddIcon width={15} height={15} />
            Nueva
          </button>

          <div className="flex-1" />
        </div>

        {/* Messages */}
        <div className="max-w-2xl mx-auto px-5 py-6 flex flex-col gap-3 min-h-[calc(100dvh-130px)]">
          {uiMessages.length === 0 && !isLoading && (
            <WelcomeScreen onSuggestion={handleSuggestion} />
          )}
          {uiMessages.map((msg, i) => (
            <MessageBubble
              key={msg.id}
              message={msg}
              onEdit={editMessage}
              onRetry={
                msg.role === "error" && i === uiMessages.length - 1 ? retryLastMessage : undefined
              }
            />
          ))}
          {isLoading && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </div>

        {showScrollBtn && (
          <button
            className="sticky bottom-4 mx-auto w-9 h-9 rounded-full bg-term-surface border border-term-border shadow-md text-term-text-dim hover:text-term-accent hover:shadow-lg cursor-pointer transition-all z-10 flex items-center justify-center"
            onClick={() =>
              chatWrapperRef.current?.scrollTo({
                top: chatWrapperRef.current.scrollHeight,
                behavior: "smooth",
              })
            }
            aria-label="Ir al final"
          >
            <ScrollDownIcon width={16} height={16} />
          </button>
        )}

        {/* Input — sticky bottom, content scrolls behind */}
        <div className="sticky bottom-0 z-10 shadow-[0_-2px_8px_-2px_rgba(0,0,0,0.06)] bg-term-bg/80 backdrop-blur-md">
          <div className="max-w-2xl mx-auto flex items-end gap-3 px-5 py-4">
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => {
                setInputValue(e.target.value);
                e.target.style.overflow = "hidden";
                e.target.style.height = "auto";
                const h = Math.min(e.target.scrollHeight, 120);
                e.target.style.height = h + "px";
                e.target.style.overflow = "";
              }}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              className="flex-1 bg-term-surface border border-term-border text-term-text font-sans text-base px-5 py-3.5 rounded-2xl resize-none min-h-5 max-h-32 overflow-y-auto placeholder:text-term-text-muted focus:border-term-accent-dim focus:shadow-md shadow-sm disabled:opacity-50 transition-shadow"
              placeholder="Escribí un mensaje..."
              rows={1}
            />
            <button
              onClick={() => void handleSend()}
              disabled={isLoading || !inputValue.trim()}
              className="w-11 h-11 bg-term-accent text-white rounded-2xl cursor-pointer hover:bg-term-accent/85 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md flex items-center justify-center shrink-0"
              aria-label="Enviar"
            >
              <SendIcon width={17} height={17} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
